import React, { FC, MouseEvent, useCallback, useMemo, useRef } from "react";
import { ScreenProps } from "@/typings";
import { DashboardView, View } from "@data-root/types";
import { Layout } from "antd";
import {
  useAppContext,
  DashboardPanelProvider,
  DashboardGroupsProvider,
  IDashboardGroupsContext,
  IDashboardLayoutsContext,
  DashboardLayoutsProvider,
  useDashboardLayoutsContext,
} from "@data-root/react";
import {
  Item,
  Menu,
  Separator,
  Submenu,
  useContextMenu,
} from "react-contexify";
import { Panel } from "@/views/DashboardView/components/panel";
import { Panels as PanelsComponent } from "@data-root/panels";

const { Content } = Layout;

const chunk = (arr: any[], k: number): any[][] => {
  const result = [];
  for (let i = 0; i < arr.length; i += k) {
    result.push(arr.slice(i, i + k));
  }
  return result;
};

export const Panels = () => {
  const { layouts } = useDashboardLayoutsContext();

  return (
    <PanelsComponent>
      {layouts.map((layout, i) => (
        <div
          key={`${layout.id}_${i}`}
          style={{ position: "relative" }}
          data-grid={layout.geometry}
        >
          <DashboardPanelProvider layout={layout}>
            <Panel />
          </DashboardPanelProvider>
        </div>
      ))}
    </PanelsComponent>
  );
};

export const DesktopDashboardView: FC<ScreenProps<DashboardView>> = ({
  view,
}) => {
  const layoutsRef = useRef<IDashboardLayoutsContext | null>(null);
  const groupsRef = useRef<IDashboardGroupsContext | null>(null);

  const { views } = useAppContext();

  const { show } = useContextMenu({ id: view.id });

  const onContextMenu = useCallback(
    (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
      show({ event });
    },
    [show]
  );

  const onClickItem = useCallback(
    ({ id }: { id: string }) => {
      const view = views.find((view) => view.id === id)!;

      layoutsRef.current.createLayout({
        id: `${view.id}-${layoutsRef.current.layouts.length}`,
        viewId: view.id,
        geometry: {
          x: 0,
          y: 0,
          w: 4,
          h: 4,
        },
      });
    },
    [views]
  );

  const menu = useMemo(() => {
    const menu: Record<string, View[]> = {};

    views.forEach((view) => {
      const prop = view.type;
      if (!menu[prop]) menu[prop] = [];
      menu[prop].push(view);
    });

    return menu;
  }, [views]);

  return (
    <DashboardLayoutsProvider ref={layoutsRef} initialLayouts={view.options.layouts}>
      <DashboardGroupsProvider
        ref={groupsRef}
        initialLayouts={view.options.layouts}
        initialGroups={view.options.experimental_groups ?? []}
      >
        <Layout style={{ background: "#f0f0f0", height: "100%" }}>
          <Content style={{ overflow: "auto" }} onContextMenu={onContextMenu}>
            <Panels />
          </Content>
        </Layout>

        <Menu id={view.id}>
          <Item
            onClick={() => {
              console.log({
                layouts: layoutsRef.current.layouts,
                groups: groupsRef.current.groups.current,
              });
            }}
            id="__export__"
            key={"__export__"}
          >
            Export
          </Item>

          <Separator />

          {Object.entries(menu).map(([type, views]) => (
            <Submenu key={type} label={type}>
              {chunk(views, 10).map((views, i) => (
                <Submenu key={i} label={(i + 1).toString()}>
                  {views.map((view, i) => (
                    <Item
                      key={`${view.id}_${i}`}
                      id={view.id}
                      onClick={onClickItem as any}
                    >
                      {view.name}
                    </Item>
                  ))}
                </Submenu>
              ))}
            </Submenu>
          ))}
        </Menu>
      </DashboardGroupsProvider>
    </DashboardLayoutsProvider>
  );
};
